<template>
  <div class="comment-list">
    <div v-if="props.isCustom"
         class="comment-user">
      <span v-if="!comment_detail.is_anonymous">{{ comment_detail.user.username }}</span>
      <span v-else>{{ $t('Comment.CommentContent.anonymousUser') }}</span>
    </div>
    <div style="display: flex;align-items: center;justify-content: space-between;">
      <div style="display: flex;align-items: baseline;">
        <a-rate v-model:value="comment_detail.rate"
                disabled />
        <div class="rate-box"
             :class="rate_title(comment_detail.rate).color">{{ rate_title(comment_detail.rate).title }}</div>
      </div>
      <div v-if="props.isCustom">
        {{ $t('Comment.CommentContent.purchased') }}
        <span v-for="(attr, index) in convertAttrs(props.item.sku_attrs)"
              :key="index"
              class="attr_color">
          {{ attr.value }}
        </span>
      </div>
      <div v-else>{{ $t('Comment.CommentContent.purchasedItem') }}{{ props.item.spu_name }}</div>
    </div>
    <div class="comment-content-box">{{ comment_detail.content }}</div>
    <div v-if="comment_detail.images"
         style="display: flex;align-items: center;">
      <div v-for="(image, index) in comment_detail.images?.split(',')"
           :key="index">
        <a-popover placement="right">
          <template #content>
            <img :src="toUrl(image)"
                 class="comment-popover" />
          </template>
          <img :src="toUrl(image)"
               class="comment-image" />
        </a-popover>
      </div>
    </div>
    <div style="display: flex;align-items: center;">
      <div class="comment-time">{{ dayjs(comment_detail.createdAt).format("YYYY-MM-DD HH:mm:ss") }}</div>
      <div v-if="!isCustom"
           class="comment-add"
           @click="appendShow">
        <SelectOutlined :rotate="270"
                        class="comment-icon" />{{ $t('Comment.CommentContent.additionalReview') }}
      </div>
      <div class="comment-add"
           :class="show_box && commentFlag.showFlag == `content${comment_detail.id}` ? 'comment-show' : ''"
           @click="replyClick">
        <MessageOutlined class="comment-icon" />{{ $t('Comment.CommentContent.reply') }}
      </div>
      <div v-if="!isOrder && !isCustom && comment_detail.rate < 4"
           class="comment-add"
           @click="editClick">
        <EditOutlined class="comment-icon" />{{ $t('Comment.CommentContent.edit') }}
      </div>
      <div v-if="!isOrder && !isCustom && comment_detail.rate < 4"
           class="comment-add"
           @click="deleteClick(comment_detail)">
        <DeleteOutlined class="comment-icon" />{{ $t('Comment.CommentContent.delete') }}
      </div>
    </div>
    <div v-if="show_box && commentFlag.showFlag == `content${comment_detail.id}`"
         class="reply-footer">
      <a-textarea v-model:value="content_value"
                  autofocus
                  :placeholder="placeholder" />
      <a-button type="primary"
                @click="submitReply">{{ $t('Comment.CommentContent.submitComment') }}</a-button>
    </div>
    <slot name="appendComment"></slot>
  </div>
  <a-divider v-if="!props.isCustom" />
</template>

<script setup lang="ts">
import { computed, ref } from "vue"
import { toUrl } from "../../../../utils/index"
import dayjs from 'dayjs'
import { Modal, message } from "ant-design-vue"
import { deleteComment, createReply } from "../api/index"
import { commentStore } from "../CommentPlugin"
import { useI18n } from 'vue-i18n'

const { t } = useI18n()
const commentFlag = commentStore()

const props = defineProps<{
  data: any
  item: any
  isOrder: boolean
  isCustom: boolean
  isAdmin: boolean
}>()

const emits = defineEmits(['appendShow', 'submitReply', 'edit'])

const appendShow = () => {
  emits('appendShow')
}

const deleteClick = (value: any) => {
  Modal.confirm({
    title: t('Comment.CommentContent.deleteConfirmTitle'),
    async onOk() {
      deleteComment(value.id)
        .then(() => {
          emits('submitReply')
        })
        .catch(() => {
        })
    },
    onCancel() {
    }
  })
}

const show_box = ref<boolean>(false)
const content_value = ref<string>('')
const placeholder = ref<string>('')

const replyClick = () => {
  show_box.value = !show_box.value
  if (commentFlag.showFlag != `reply${comment_detail.value.id}`) {
    show_box.value = true
  }
  const content = comment_detail.value.is_anonymous ? t('Comment.CommentContent.anonymousUser') : comment_detail.value.user.username
  placeholder.value = t('Comment.CommentContent.reply') + content + '：'
  commentFlag.showFlag = `content${comment_detail.value.id}`
}

const submitReply = async () => {
  if (!content_value.value) {
    message.error(t('Comment.CommentContent.enterCommentMessage'))
  }
  const data = {
    content: content_value.value,
    reply_user_id: comment_detail.value.user.id,
    is_admin: props.isAdmin,
    comment_id: comment_detail.value.id,
    index: 1
  }
  await createReply(comment_detail.value.id, data)
  emits('submitReply')
  show_box.value = false
}

const convertAttrs = (attrs: any[]) => {
  const arr = [] as any
  attrs.forEach((a) => {
    if (a.attribute.key != 'weight') {
      const data = {
        name: '',
        value: ''
      }
      data.name = a.attribute.name
      data.value = a.object_value ? a.object_value.name : a.value
      arr.push(data)
    }
  })
  arr.sort(sortBy('name', false))
  return arr
}

const editClick = () => {
  emits('edit')
}

const rate_title = (data: any) => {
  let title = t('Comment.CommentContent.goodReview')
  let color = 'green'
  if (data == 5 || data == 4) {
    title = t('Comment.CommentContent.goodReview')
    color = 'green'
  } else if (data == 3 || data == 2) {
    title = t('Comment.CommentContent.averageReview')
    color = 'orange'
  } else if (data == 1) {
    title = t('Comment.CommentContent.badReview')
    color = 'red'
  }
  return { title, color }
}

const comment_detail = computed(() => {
  return props.data[0]
})

const sortBy = (attr: any, rev: any) => {
  // 第二个参数没有传递 默认升序排列
  if (rev == undefined) {
    rev = 1
  } else {
    rev = (rev) ? 1 : -1
  }

  return function (a: any, b: any) {
    a = a[attr]
    b = b[attr]
    if (a < b) {
      return rev * -1
    }
    if (a > b) {
      return rev * 1
    }
    return 0
  }
}
</script>

<style scoped lang="less">
.comment-list {
  .rate-box {
    margin-left: 20px;
  }

  .attr_color {
    margin-right: 10px;
  }

  .green {
    color: #00C939;
  }

  .orange {
    color: #EA9A00;
  }

  .red {
    color: #DC2020;
  }

  .comment-user {
    font-size: 14px;
    height: 32px;
    line-height: 32px;
    font-weight: bold;
  }

  .comment-content-box {
    width: 100%;
    height: 100%;
    font-size: 14px;
    line-height: 24px;
    margin: 10px 0;
    word-break: break-all;
  }

  .comment-time {
    font-size: 14px;
    color: #CCCCCC;
  }

  .comment-add {
    font-size: 14px;
    color: #CCCCCC;
    margin-left: 20px;
    cursor: pointer;
  }

  .comment-show {
    color: #111;
  }

  .comment-icon {
    margin-right: 5px;
  }

  .comment-image {
    width: 50px;
    height: 50px;
    margin: 0 10px 10px 0;
    cursor: pointer;
  }
}

.reply-footer {
  display: flex;
  margin-top: 20px;
  background-color: #f6f6f6;

  .ant-input {
    flex: 1;
    height: 64px;
    background: #f6f6f6;
  }

  .ant-btn {
    width: 64px;
    height: 64px;
    text-wrap: wrap;
  }
}

.ant-popover {
  width: 260px;

  .comment-popover {
    max-width: 260px;
    // height: 200px;
  }
}
</style>
